import { formatDate } from '@dawn/core/utils';
import { MCP_PERMISSION } from '@dawn/feature-config';
import {
  Banner,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Spinner,
} from '@dawn/ui';
import { Plug, Trash2 } from 'lucide-react';

import { useRevokeMcpGrant } from '@/hooks/mutations/use-revoke-mcp-grant';
import { useMcpGrantsQuery } from '@/hooks/queries/use-mcp-grants-query';
import { usePermissions } from '@/hooks/use-permissions';
import type { McpGrantScope } from '@/lib/api';

import { McpConnectionInstructions } from './McpConnectionInstructions';

/**
 * Settings → MCP → Connected apps (TDEV-1199): the MCP clients authorized over
 * OAuth. `users.manage` (Super Admin, TDEV-1213 step 18) sees every Admin's;
 * anyone else here holds `MCP_PERMISSION` and sees their own (TDEV-1234) — the
 * server decides what each list holds, the browser only picks which to ask for.
 * The connection help and the invitation to connect show only to those who
 * may connect. Revoke disconnects the app immediately (its next call is a 401).
 * A revoked grant is dead, so it is dropped from the list.
 */
export function McpConnectedAppsSection() {
  const { can } = usePermissions();
  const canManage = can('users.manage');
  const canConnect = can(MCP_PERMISSION);
  const scope: McpGrantScope = canManage ? 'tenant' : 'own';
  const { data: allGrants = [], isLoading, isError, refetch } = useMcpGrantsQuery(scope);
  const revokeGrant = useRevokeMcpGrant(scope);
  const grants = allGrants.filter((grant) => grant.status !== 'revoked');

  return (
    <Card className="rounded-xl">
      <CardHeader className="space-y-1.5">
        <CardTitle>Connected apps</CardTitle>
        <CardDescription>
          {canManage
            ? "MCP clients that signed in to your organization's data with OAuth."
            : "MCP clients you connected to your organization's data with OAuth."}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {revokeGrant.isError && (
          <Banner
            variant="error"
            className="mb-4 rounded-md border"
            data-testid="mcp-grants-revoke-error"
          >
            Couldn&apos;t disconnect the app. Please try again.
          </Banner>
        )}
        {isLoading ? (
          <div className="flex items-center justify-center py-12" data-testid="mcp-grants-loading">
            <Spinner animate size={32} />
          </div>
        ) : isError ? (
          <Banner
            variant="error"
            className="items-start rounded-md border"
            data-testid="mcp-grants-error"
          >
            <div className="flex flex-1 flex-col items-start gap-2">
              <span>Couldn&apos;t load your connected apps.</span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => void refetch()}
                data-testid="mcp-grants-retry-button"
              >
                Retry
              </Button>
            </div>
          </Banner>
        ) : grants.length === 0 ? (
          <div
            className="flex flex-col items-center justify-center gap-3 py-12 text-center"
            data-testid="mcp-grants-empty"
          >
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-orange-100">
              <Plug className="h-6 w-6 text-dawn-orange" />
            </div>
            {canConnect ? (
              <div>
                <p className="text-sm font-medium text-dawn-black">No connected apps yet</p>
                <p className="text-sm text-muted-foreground">
                  Connect an MCP client below — it asks you to allow access in the browser.
                </p>
              </div>
            ) : (
              <div>
                <p className="text-sm font-medium text-dawn-black">No connected apps</p>
                <p className="text-sm text-muted-foreground">
                  Nobody in your organization has connected an MCP client yet.
                </p>
              </div>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">
                    App
                  </th>
                  {canManage && (
                    <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">
                      Admin
                    </th>
                  )}
                  <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">
                    Connected
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">
                    Last used
                  </th>
                  <th className="px-4 py-3 text-right text-sm font-medium text-muted-foreground">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {grants.map((grant) => (
                  <tr
                    key={grant.id}
                    className="border-b last:border-0"
                    data-testid={`mcp-grant-row-${grant.id}`}
                  >
                    <td className="px-4 py-3 text-sm">
                      <span className="inline-flex items-center gap-2">
                        {grant.clientName}
                        {grant.status === 'expired' && (
                          <span className="inline-flex items-center rounded-full bg-error/10 px-2 py-0.5 text-xs font-medium text-error">
                            Expired
                          </span>
                        )}
                      </span>
                    </td>
                    {canManage && (
                      <td className="px-4 py-3 text-sm text-muted-foreground">
                        {grant.adminEmail}
                      </td>
                    )}
                    <td className="px-4 py-3 text-sm text-muted-foreground">
                      {formatDate(grant.createdAt)}
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">
                      {grant.lastUsedAt ? formatDate(grant.lastUsedAt) : '—'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label={
                          canManage
                            ? `Disconnect ${grant.clientName} (${grant.adminEmail})`
                            : `Disconnect ${grant.clientName}`
                        }
                        onClick={() => revokeGrant.mutate(grant.id)}
                        disabled={revokeGrant.isPending}
                        data-testid={`mcp-grant-revoke-button-${grant.id}`}
                      >
                        <Trash2 className="h-4 w-4 text-error" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {canConnect && <McpConnectionInstructions />}
      </CardContent>
    </Card>
  );
}
